/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResponsivePie } from '@nivo/pie';

import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import colorSelector from '../../utils/formatCharts/handleColor';
import { labelValuesStr } from '../../utils/formatCharts/handleCustomValue';
import { dataSort, genData1D } from '../../utils/formatCharts/handleData';
import KpiStatus from '../../utils/chartTools/KpiStatus';

const emptyObj = {};

const Pie = ({
  data, styleConfig, isEdit, globalConfig = emptyObj, handleDrilldownClick = null,
}) => {
  const [chartData, setChartData] = useState({ ready: false });

  useEffect(() => {
    setChartData(genData1D(data, styleConfig));
  }, [
    data,
    styleConfig.DataFormat,
    styleConfig.DataFormatAlt,
    styleConfig.DataAbsoluteFormatAlt,
  ]);

  useEffect(() => {
    if (chartData.ready) {
      setChartData((cData) => ({
        ...cData,
        data: dataSort(chartData.data, styleConfig),
      }));
    }
  }, [
    ...styleConfig.CustomSortControl.kpiCategories,
    styleConfig.SortValuesControl,
  ]);

  const createLabel = (label) => {
    if (
      styleConfig.EnableLabelMinControl
      && (label.data.value * 100) / chartData.total < (styleConfig.LabelMinPercentControl ?? 0)
    ) return null;
    return labelValuesStr(label.data.value, label.data.value2, styleConfig);
  };

  const createTooltip = (val) => customTooltip(val, 'pie', styleConfig, handleDrilldownClick);

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          'NoLineSelected',
          data?.index?.[0] === 'Totais',
        ],
        [
          'HasNegativeValues',
          chartData.hasNegative,
        ],
      ])}
    >
      <ResponsivePie
        data={chartData.data}
        margin={
          styleConfig?.Margin || {
            top: 50,
            right: 130,
            bottom: 50,
            left: 60,
          }
        }
        onClick={handleDrilldownClick ? ((dataPoint) => {
          handleDrilldownClick(dataPoint?.data?.id);
        }) : null}
        startAngle={-45}
        padAngle={styleConfig?.PaddingAngleControl?.value}
        cornerRadius={styleConfig?.CornerRadiusControl?.value}
        innerRadius={styleConfig?.InnerRadiusControl?.value}
        borderWidth={styleConfig?.BorderWidthControl?.value}
        // TOOLTIPS
        isInteractive={styleConfig?.InteractiveControl}
        tooltip={createTooltip}
        enableArcLinkLabels={styleConfig?.RadialLabelControl?.checked}
        enableArcLabels={styleConfig?.EnableLabelControl?.checked}
        colors={colorSelector({
          dataLength: chartData.data?.length,
          config: styleConfig,
          colorKey: 'id',
          globalConfig,
        })}
        borderColor={
          styleConfig.BorderColorControl
            ? styleConfig.BorderColorControl
            : { from: 'color', modifiers: [['darker', 1.6]] }
        }
        // LABELs
        arcLabel={createLabel}
        arcLabelsTextColor={styleConfig?.SliceTextColorControl}
        arcLinkLabelsTextColor={styleConfig?.LabelTextColorControl}
        arcLinkLabelsOffset={6}
        arcLinkLabelsTextOffset={8}
        arcLinkLabelsDiagonalLength={24}
        arcLinkLabelsStraightLength={24}
        arcLinkLabelsThickness={1}
        arcLinkLabelsColor={{ from: 'color' }}
        arcLabelsSkipAngle={0}
        legends={styleConfig?.LegendControl?.checked ? [{
          ...styleConfig?.LegendPlacementControl?.value,
          itemWidth: 100,
          itemHeight: 18,
          symbolSize: 18,
          symbolShape: styleConfig?.LegendSymbolControl || 'circle',
          itemsSpacing: parseInt(styleConfig?.LegendsSpacingControl?.value, 10),
          justify: false,
          effects: [{
            on: 'hover',
            style: {
              itemTextColor: '#000',
            },
          }],
          toggleSerie: !isEdit,
        }] : []}
        theme={{
          labels: {
            text: {
              fontSize: parseInt(styleConfig?.LabelsFontSizeControl?.value, 10),
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig?.LabelsFontFamilyControl?.value}', Arial`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          legends: {
            text: {
              fontSize: `${styleConfig?.LegendsFontSizeControl?.value}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLegend : styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
        }}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
        activeOuterRadiusOffset={styleConfig.InteractiveControl && !!styleConfig.Animate
          ? (+styleConfig.PartExtension || 0) : 0}
      />
    </KpiStatus>
  ) : <></>;
};

Pie.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  isEdit: PropTypes.bool.isRequired,
  globalConfig: PropTypes.objectOf(PropTypes.any),
  handleDrilldownClick: PropTypes.func,
};

export default Pie;
